import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { Card, CardBody, Row, Col } from "reactstrap";
import NonAdherentPatientTimelineShimmer from "./NonAdherentPatientTimelineShimmer";

const NonAdherentPatientTimelineReport = ({ selectedPatientId, timelineData, isLoading }) => {
  const [viewMonths, setViewMonths] = useState("all");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [canOverviewScrollLeft, setCanOverviewScrollLeft] = useState(false);
  const [canOverviewScrollRight, setCanOverviewScrollRight] = useState(false);
  const timelineScrollRef = useRef(null);
  const overviewScrollRef = useRef(null);
  const isSyncingScrollRef = useRef(false);
  const SCROLL_EDGE_EPSILON = 8;
  const report = timelineData?.data || timelineData || {};
  const patient = report?.patient || report?.patient_info || {};
  const isNonAdherentStatus = (status) => {
    const normalized = String(status || "").trim().toLowerCase().replace(/_/g, "-");
    return normalized === "non-adherent" || normalized === "notmet";
  };
  const formatPatientDisplayName = (rawName) => {
    const name = String(rawName || "").trim();
    if (!name) return "-";

    if (name.includes(",")) {
      const [last = "", first = ""] = name.split(",").map((part) => String(part || "").trim());
      return `${last}${first ? `, ${first}` : ""}`;
    }

    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length < 2) return name;

    const last = parts[parts.length - 1];
    const first = parts.slice(0, -1).join(" ");
    return `${last}, ${first}`;
  };

  const timelineRows = Array.isArray(report?.timeline) ? report.timeline : [];
  const kpiRaw = report?.kpi || {};
  const streak = report?.streak_summary?.current_streak || {};

  const totalMonths = Number(kpiRaw?.total_months || timelineRows.length || 0);
  const technicalMetMonths = Number(
    kpiRaw?.technical_met_months !== undefined
      ? kpiRaw.technical_met_months
      : timelineRows.filter((row) => row?.technical?.present).length
  );
  const professionalMetMonths = Number(
    kpiRaw?.professional_met_months !== undefined
      ? kpiRaw.professional_met_months
      : timelineRows.filter((row) => row?.professional?.present).length
  );
  const bothMetMonths = Number(
    kpiRaw?.both_components_met_months !== undefined
      ? kpiRaw.both_components_met_months
      : timelineRows.filter((row) => row?.technical?.present && row?.professional?.present).length
  );
  const onlyOneMetMonths = Number(
    kpiRaw?.only_one_component_met_months !== undefined
      ? kpiRaw.only_one_component_met_months
      : timelineRows.filter((row) => Boolean(row?.technical?.present) !== Boolean(row?.professional?.present)).length
  );
  const nonAdherentMonths = Number(
    kpiRaw?.non_adherent_months !== undefined
      ? kpiRaw.non_adherent_months
      : timelineRows.filter((row) => isNonAdherentStatus(row?.status)).length
  );
  const adherentMonths = Number(
    kpiRaw?.adherent_months !== undefined
      ? kpiRaw.adherent_months
      : Math.max(totalMonths - nonAdherentMonths, 0)
  );

  const getPct = (value) => (totalMonths > 0 ? `${((value / totalMonths) * 100).toFixed(1)}% of total` : "0.0% of total");

  const hasReportData = Boolean(
    (patient && (patient.patient_name || patient.patientName || patient._id)) ||
    timelineRows.length ||
    Object.keys(kpiRaw).length
  );

  const sortedTimelineRows = useMemo(
    () => [...timelineRows].sort((a, b) => String(b?.month || "").localeCompare(String(a?.month || ""))),
    [timelineRows]
  );
  const getTimelineCategory = (row) => {
    if (row?.in_streak) return "streak";
    const technical = Boolean(row?.technical?.present);
    const professional = Boolean(row?.professional?.present);
    if (technical && professional) return "both";
    if (technical || professional) return "partial";
    if (isNonAdherentStatus(row?.status)) return "missing";
    return "none";
  };
  const getTimelineVisualClass = (row) => (isCurrentMonth(row?.month) ? "streak" : getTimelineCategory(row));
  const visibleTimelineRows = useMemo(() => {
    if (viewMonths === "all") return sortedTimelineRows;
    const count = Number(viewMonths) || sortedTimelineRows.length;
    return sortedTimelineRows.slice(0, count);
  }, [sortedTimelineRows, viewMonths]);
  const timelineColumns = [...visibleTimelineRows].reverse();
  const overviewColumns = timelineColumns;
  useEffect(() => {
    if (!timelineColumns.length) {
      setSelectedMonth("");
      return;
    }
    if (!selectedMonth || !timelineColumns.some((row) => row?.month === selectedMonth)) {
      setSelectedMonth(timelineColumns[timelineColumns.length - 1]?.month || "");
    }
  }, [timelineColumns, selectedMonth]);

  const technicalConfigCodes = Array.isArray(report?.cpt_configuration?.technical_cpt_codes)
    ? report.cpt_configuration.technical_cpt_codes
    : [];
  const professionalConfigCodes = Array.isArray(report?.cpt_configuration?.professional_cpt_codes)
    ? report.cpt_configuration.professional_cpt_codes
    : [];
  const longestStreak = report?.streak_summary?.longest_streak || {};
  const adherentTimelineRows = timelineColumns.filter((item) => !isNonAdherentStatus(item?.status));
  const nonAdherentTimelineRows = timelineColumns.filter((item) => isNonAdherentStatus(item?.status));
  const lastAdherentRow = timelineRows.find((item) => !isNonAdherentStatus(item?.status));
  const allMonthsAdherent = timelineColumns.length > 0
    && timelineColumns.every((item) => !isNonAdherentStatus(item?.status));

  const getComponentDisplay = (monthRow, type, configuredCodes) => {
    const component = type === "technical" ? monthRow?.technical : monthRow?.professional;
    const metCodes = new Set(
      Array.isArray(component?.met_cpt_codes)
        ? component.met_cpt_codes.map((code) => String(code))
        : []
    );
    const notMetCodes = new Set(
      Array.isArray(component?.not_met_cpt_codes)
        ? component.not_met_cpt_codes.map((code) => String(code))
        : []
    );
    const allCodes = Array.isArray(component?.all_cpt_codes) ? component.all_cpt_codes.map((code) => String(code)) : [];
    const fallbackCodes = Array.isArray(component?.cpt_codes) ? component.cpt_codes.map((code) => String(code)) : [];
    const sourceCodes = allCodes.length
      ? allCodes
      : (configuredCodes.length ? configuredCodes : fallbackCodes);

    if (!sourceCodes.length) return { items: [] };

    const items = sourceCodes.map((code) => {
      if (metCodes.has(code)) {
        return { code, isMet: true };
      }
      if (notMetCodes.has(code)) {
        return { code, isMet: false };
      }
      return { code, isMet: Boolean(component?.present) };
    });

    return { items };
  };

  const serializeComponentItems = (monthRow, type, configuredCodes) => {
    const display = getComponentDisplay(monthRow, type, configuredCodes);
    if (!display.items.length) return "-";
    const uniqueItems = display.items.reduce((acc, item) => {
      const code = String(item.code || "").trim();
      if (!code) return acc;
      const key = `${code}-${item.isMet ? "met" : "notmet"}`;
      if (!acc.some((entry) => entry.key === key)) {
        acc.push({ key, value: `${code} (${item.isMet ? "Met" : "Not Met"})` });
      }
      return acc;
    }, []);
    return uniqueItems
      .map((item) => item.value)
      .join(" | ");
  };

  const escapeCsvValue = (value) => {
    const normalized = value === null || value === undefined ? "" : String(value);
    if (/[",\n]/.test(normalized)) {
      return `"${normalized.replace(/"/g, "\"\"")}"`;
    }
    return normalized;
  };

  const handleExportTimelineCsv = () => {
    if (!hasReportData || !timelineColumns.length) return;

    const headers = [
      "Patient Name",
      "Provider",
      "Clinic",
      "Timeline Month",
      "Timeline Status",
      "Technical CPT Details",
      "Professional CPT Details"
    ];

    const csvRows = timelineColumns.map((row) => ([
      patient?.patient_name || patient?.patientName || "-",
      patient?.provider_name || patient?.mainCareProvider?.name || "-",
      patient?.clinic_name || patient?.client?.organization || "-",
      moment(row?.month, "YYYY-MM").isValid() ? moment(row.month, "YYYY-MM").format("MMM YYYY") : (row?.month || "-"),
      isNonAdherentStatus(row?.status) ? "Non-Adherent" : "Adherent",
      serializeComponentItems(row, "technical", technicalConfigCodes),
      serializeComponentItems(row, "professional", professionalConfigCodes)
    ]));

    const csvContent = [
      headers.map(escapeCsvValue).join(","),
      ...csvRows.map((row) => row.map(escapeCsvValue).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const safePatientName = String(patient?.patient_name || patient?.patientName || "patient")
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^a-zA-Z0-9-]/g, "")
      || "patient";
    const fileName = `${safePatientName}_adheranceAndNonAtherance_report.csv`;
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.setAttribute("download", fileName);
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  const renderComponentCell = (row, type, configuredCodes) => {
    const display = getComponentDisplay(row, type, configuredCodes);
    if (!display.items.length) {
      return (
        <div className="nax-comp-cell">
          <span className="nax-comp-code missing is-empty">—</span>
        </div>
      );
    }

    return (
      <div className="nax-comp-cell">
        <div className="nax-comp-list">
          {display.items.map((item, index) => (
            <div key={`${row?.month}-${type}-${item.code}-${index}`} className={`nax-comp-item ${item.isMet ? "met" : "missing"}`}>
              <i className={`mdi ${item.isMet ? "mdi-check-circle" : "mdi-close-circle"} nax-comp-item-icon`}></i>
              <span className="nax-comp-item-code">{item.code || "-"}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const getMonthStatusClass = (status) => (isNonAdherentStatus(status) ? "non-adherent" : "adherent");
  const scrollTimeline = (direction) => {
    if (!timelineScrollRef.current) return;
    const step = 320;
    timelineScrollRef.current.scrollBy({ left: direction * step, behavior: "smooth" });
  };
  const syncHorizontalScroll = (sourceEl, targetEl) => {
    if (!sourceEl || !targetEl) return;
    const sourceMax = Math.max(sourceEl.scrollWidth - sourceEl.clientWidth, 0);
    const targetMax = Math.max(targetEl.scrollWidth - targetEl.clientWidth, 0);
    const ratio = sourceMax > 0 ? sourceEl.scrollLeft / sourceMax : 0;
    targetEl.scrollLeft = ratio * targetMax;
  };
  const getHorizontalScrollState = (el) => {
    if (!el) return { canScrollLeft: false, canScrollRight: false };
    const maxScrollLeft = Math.max(el.scrollWidth - el.clientWidth, 0);
    // Clamp and round to avoid float precision issues near the right edge.
    const currentScrollLeft = Math.min(Math.max(Math.ceil(el.scrollLeft), 0), maxScrollLeft);
    return {
      canScrollLeft: currentScrollLeft > SCROLL_EDGE_EPSILON,
      canScrollRight: maxScrollLeft - currentScrollLeft > SCROLL_EDGE_EPSILON
    };
  };
  const updateTimelineScrollState = () => {
    const el = timelineScrollRef.current;
    if (!el) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }
    const nextState = getHorizontalScrollState(el);
    setCanScrollLeft(nextState.canScrollLeft);
    setCanScrollRight(nextState.canScrollRight);
  };
  const scrollOverview = (direction) => {
    if (!overviewScrollRef.current) return;
    overviewScrollRef.current.scrollBy({ left: direction * 240, behavior: "smooth" });
  };
  const onOverviewWheel = (event) => {
    if (!overviewScrollRef.current) return;
    if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
      event.preventDefault();
      overviewScrollRef.current.scrollBy({ left: event.deltaY, behavior: "auto" });
    }
  };
  const updateOverviewScrollState = () => {
    const el = overviewScrollRef.current;
    if (!el) {
      setCanOverviewScrollLeft(false);
      setCanOverviewScrollRight(false);
      return;
    }
    const nextState = getHorizontalScrollState(el);
    setCanOverviewScrollLeft(nextState.canScrollLeft);
    setCanOverviewScrollRight(nextState.canScrollRight);
  };
  const scrollSelectedMonthIntoView = (month, behavior = "smooth") => {
    if (!month) return;
    const overviewEl = overviewScrollRef.current;
    const timelineEl = timelineScrollRef.current;
    if (overviewEl) {
      const selectedOverviewButton = overviewEl.querySelector(`[data-month="${month}"]`);
      if (selectedOverviewButton) {
        selectedOverviewButton.scrollIntoView({
          behavior,
          block: "nearest",
          inline: "center"
        });
      }
    }
    if (timelineEl) {
      const selectedTimelineHeader = timelineEl.querySelector(`th[data-month="${month}"]`);
      if (selectedTimelineHeader) {
        const targetLeft = Math.max(
          0,
          selectedTimelineHeader.offsetLeft - ((timelineEl.clientWidth - selectedTimelineHeader.clientWidth) / 2)
        );
        timelineEl.scrollTo({ left: targetLeft, behavior });
      }
    }
    window.requestAnimationFrame(() => {
      updateTimelineScrollState();
      updateOverviewScrollState();
    });
  };
  const handleMonthSelection = (month) => {
    if (!month) return;
    setSelectedMonth(month);
  };
  const parseMonthValue = (monthValue) => {
    const parsed = moment(monthValue, ["YYYY-MM", "YYYY-MM-DD"], true);
    return parsed.isValid() ? parsed : moment(monthValue);
  };
  const isCurrentMonth = (monthValue) => {
    const parsed = parseMonthValue(monthValue);
    return parsed.isValid() && parsed.isSame(moment(), "month");
  };
  useEffect(() => {
    const el = timelineScrollRef.current;
    if (!el) return undefined;
    const onScroll = () => {
      updateTimelineScrollState();
      if (isSyncingScrollRef.current) return;
      isSyncingScrollRef.current = true;
      syncHorizontalScroll(el, overviewScrollRef.current);
      window.requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    };
    updateTimelineScrollState();
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [timelineColumns.length, viewMonths]);
  useEffect(() => {
    const rafId = window.requestAnimationFrame(() => {
      updateTimelineScrollState();
    });
    return () => window.cancelAnimationFrame(rafId);
  }, [timelineColumns]);
  useEffect(() => {
    const el = overviewScrollRef.current;
    if (!el) return undefined;
    const onScroll = () => {
      updateOverviewScrollState();
      if (isSyncingScrollRef.current) return;
      isSyncingScrollRef.current = true;
      syncHorizontalScroll(el, timelineScrollRef.current);
      window.requestAnimationFrame(() => {
        isSyncingScrollRef.current = false;
      });
    };
    updateOverviewScrollState();
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [overviewColumns.length, viewMonths]);
  useEffect(() => {
    const rafId = window.requestAnimationFrame(() => {
      updateOverviewScrollState();
    });
    return () => window.cancelAnimationFrame(rafId);
  }, [overviewColumns]);
  useEffect(() => {
    const timelineEl = timelineScrollRef.current;
    const overviewEl = overviewScrollRef.current;
    if (!timelineEl || !overviewEl) return undefined;
    const rafId = window.requestAnimationFrame(() => {
      const timelineMax = Math.max(timelineEl.scrollWidth - timelineEl.clientWidth, 0);
      const overviewMax = Math.max(overviewEl.scrollWidth - overviewEl.clientWidth, 0);
      timelineEl.scrollLeft = timelineMax;
      overviewEl.scrollLeft = overviewMax;
      updateTimelineScrollState();
      updateOverviewScrollState();
    });
    return () => window.cancelAnimationFrame(rafId);
  }, [selectedPatientId, timelineColumns.length, overviewColumns.length, viewMonths]);
  useEffect(() => {
    if (!selectedMonth) return;
    const rafId = window.requestAnimationFrame(() => {
      scrollSelectedMonthIntoView(selectedMonth, "smooth");
    });
    return () => window.cancelAnimationFrame(rafId);
  }, [selectedMonth, timelineColumns.length]);
  const getTimelineDotClass = (row) => getTimelineCategory(row);
  const getTimelineDotIcon = (row) => {
    const dotClass = getTimelineVisualClass(row);
    if (dotClass === "streak") return "mdi mdi-checkbox-blank";
    if (dotClass === "both") return "mdi mdi-check-circle";
    if (dotClass === "missing") return "mdi mdi-close-circle";
    if (dotClass === "partial") return "mdi mdi-minus";
    return "mdi mdi-minus";
  };
  return (
    <Card className="details-table-card-modern mt-3">
      <CardBody>
        {!selectedPatientId ? (
          <div className="mpr-empty-row">Click "View Report" from Action column to load patient timeline report.</div>
        ) : isLoading ? (
          <NonAdherentPatientTimelineShimmer />
        ) : !hasReportData ? (
          <div className="na-report-empty-state">
            <div className="na-report-empty-icon">
              <i className="mdi mdi-file-chart-outline"></i>
            </div>
            <h5>No report data loaded</h5>
            <p>Click <strong>View Report</strong> in Action column to fetch this patient's timeline and KPI details.</p>
          </div>
        ) : (
          <>
            <div className="nax-page-head">
              <div className="nax-page-title-wrap">
                <div className="nax-page-title-icon">
                  <i className="mdi mdi-chart-timeline-variant"></i>
                </div>
                <div className="nax-page-title-copy">
                <h4>Patient Adherence/Non-Adherence Timeline</h4>
                <p>Patient's CPT adherence/non-adherence timeline and KPI details.</p>
                </div>
              </div>
              <div>
                <button
                  type="button"
                  className="nax-export-btn"
                  onClick={handleExportTimelineCsv}
                  disabled={isLoading || !timelineColumns.length}
                >
                  <i className="mdi mdi-download"></i>
                  <span>Export CSV</span>
                </button>
              </div>
            </div>

            <div className="nax-top-grid">
              <div className="nax-card nax-patient-card">
                <div className="nax-patient-avatar">{String(patient?.patient_name || patient?.patientName || "P").charAt(0)}</div>
                <div className="nax-patient-copy">
                  <div className="nax-patient-title-row">
                    <h5>{formatPatientDisplayName(patient?.patient_name || patient?.patientName)}</h5>
                    <span className="nax-status-pill non-adherent">Non-Adherent</span>
                  </div>
                  <div className="nax-patient-meta">
                    <div className="nax-meta-item">
                      <i className="mdi mdi-cake-variant"></i>
                      <div><label>DOB</label><strong>{patient?.date_of_birth || "-"}</strong></div>
                    </div>
                    <div className="nax-meta-item">
                      <i className="mdi mdi-calendar"></i>
                      <div><label>Enrolled Date</label><strong>{patient?.enrolledDate || "-"}</strong></div>
                    </div>
                    <div className="nax-meta-item">
                      <i className="mdi mdi-shield-account"></i>
                      <div><label>Insurance</label><strong>{patient?.insuranceProvider || "-"}</strong></div>
                    </div>
                    <div className="nax-meta-item">
                      <i className="mdi mdi-upload"></i>
                      <div><label>Last Upload</label><strong>{patient?.lastUploadDate || "-"}</strong></div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="nax-card nax-provider-card">
                <div><strong><i className="mdi mdi-account-heart-outline"></i> Care Provider</strong><p>{patient?.provider_name || patient?.mainCareProvider?.name || "-"}</p></div>
                <div><strong><i className="mdi mdi-hospital-building"></i> Clinic</strong><p>{patient?.clinic_name || patient?.client?.organization || "-"}</p></div>
              </div>

              <div className="nax-card nax-streak-grid">
                <div>
                  <span><i className="mdi mdi-chart-line"></i> Adherence Months</span>
                  <h4>{adherentMonths || 0} <small>Months</small></h4>
                  <p>{adherentTimelineRows[0]?.month && adherentTimelineRows[adherentTimelineRows.length - 1]?.month ? `${moment(adherentTimelineRows[0].month, "YYYY-MM").format("MMM YYYY")} - ${moment(adherentTimelineRows[adherentTimelineRows.length - 1].month, "YYYY-MM").format("MMM YYYY")}` : "-"}</p>
                </div>
                <div>
                  <span><i className="mdi mdi-fire"></i> Non-Adherent Months</span>
                  <h4>{nonAdherentMonths || 0} <small>Months</small></h4>
                  <p>{nonAdherentTimelineRows[0]?.month && nonAdherentTimelineRows[nonAdherentTimelineRows.length - 1]?.month ? `${moment(nonAdherentTimelineRows[0].month, "YYYY-MM").format("MMM YYYY")} - ${moment(nonAdherentTimelineRows[nonAdherentTimelineRows.length - 1].month, "YYYY-MM").format("MMM YYYY")}` : "-"}</p>
                </div>
              </div>
            </div>

            <div className="nax-kpi-row">
              <div className="nax-card">
                <strong><i className="mdi mdi-tune-variant"></i> CPT Configuration</strong>
                <div className="nax-config-grid">
                  <div>
                    <label>Technical CPT Codes</label>
                    <div className="nax-chip-row">{technicalConfigCodes.map((code) => <span key={code} className="nax-mini-chip">{code}</span>)}</div>
                  </div>
                  <div>
                    <label>Professional CPT Codes</label>
                    <div className="nax-chip-row">{professionalConfigCodes.map((code) => <span key={code} className="nax-mini-chip">{code}</span>)}</div>
                  </div>
                </div>
              </div>
              <div className="nax-card nax-metric-card tech">
                <span><i className="mdi mdi-tools"></i> Technical Component</span>
                <small className="nax-metric-sub">Met in</small>
                <h4>{technicalMetMonths}<small> Months</small></h4>
                <div className="nax-metric-divider"></div>
                <small className="nax-metric-sub">Not met in</small>
                <p className="nax-metric-notmet"><strong>{Math.max(totalMonths - technicalMetMonths, 0)}</strong><span>Months</span></p>
              </div>
              <div className="nax-card nax-metric-card prof">
                <span><i className="mdi mdi-briefcase-account-outline"></i> Professional Component</span>
                <small className="nax-metric-sub">Met in</small>
                <h4>{professionalMetMonths}<small> Months</small></h4>
                <div className="nax-metric-divider"></div>
                <small className="nax-metric-sub">Not met in</small>
                <p className="nax-metric-notmet"><strong>{Math.max(totalMonths - professionalMetMonths, 0)}</strong><span>Months</span></p>
              </div>
              <div className="nax-card nax-metric-card both">
                <span><i className="mdi mdi-shield-check-outline"></i> Both Components</span>
                <small className="nax-metric-sub">Met in</small>
                <h4>{bothMetMonths}<small> Months</small></h4>
                <div className="nax-metric-divider"></div>
                <small className="nax-metric-sub">Not met in</small>
                <p className="nax-metric-notmet"><strong>{Math.max(totalMonths - bothMetMonths, 0)}</strong><span>Months</span></p>
              </div>
            </div>

            <div className="nax-card nax-timeline-section">
              <div className="nax-timeline-head">
                <div className="nax-timeline-head-left">
                  <h5>
                    <span className="nax-timeline-title-icon">
                      <i className="mdi mdi-chart-timeline-variant"></i>
                    </span>
                    Adherence Timeline
                  </h5>
                  <div className="nax-legend-row">
                    <span><i className="mdi mdi-check-circle nax-dot met"></i> Both Met (Adherent)</span>
                    <span><i className="mdi mdi-minus nax-dot partial"></i> Partial</span>
                    <span><i className="mdi mdi-close-circle nax-dot missing"></i> Missing Either</span>
                    <span><i className="mdi mdi-minus-circle nax-dot none"></i> No Activity</span>
                    <span><i className="mdi mdi-checkbox-blank nax-dot streak"></i> In Current Month</span>
                  </div>
                </div>
                <div className="nax-view-control">
                  <label htmlFor="timeline-view-months">View</label>
                  <select
                    id="timeline-view-months"
                    className="nax-view-select"
                    value={viewMonths}
                    onChange={(e) => setViewMonths(e.target.value)}
                  >
                    <option value="6">6 Months</option>
                    <option value="12">12 Months</option>
                    <option value="24">24 Months</option>
                    <option value="all">All</option>
                  </select>
                </div>
              </div>
              <div className="nax-overview-strip">
                <div className="nax-overview-meta">
                  <strong>Overall Overview</strong>
                  <span>
                    {overviewColumns.length
                      ? `${moment(overviewColumns[0]?.month, "YYYY-MM").format("MMM YYYY")} - ${moment(overviewColumns[overviewColumns.length - 1]?.month, "YYYY-MM").format("MMM YYYY")}`
                      : "-"}
                  </span>
                </div>
                <div className="nax-overview-track">
                  <div className="nax-overview-line"></div>
                  <div className="nax-overview-streak-line"></div>
                  {canOverviewScrollLeft ? (
                    <button
                      type="button"
                      className="nax-overview-nav-btn left"
                      aria-label="Scroll overview left"
                      onClick={() => scrollOverview(-1)}
                    >
                      <i className="mdi mdi-chevron-left"></i>
                    </button>
                  ) : null}
                  {canOverviewScrollRight ? (
                    <button
                      type="button"
                      className="nax-overview-nav-btn right"
                      aria-label="Scroll overview right"
                      onClick={() => scrollOverview(1)}
                    >
                      <i className="mdi mdi-chevron-right"></i>
                    </button>
                  ) : null}
                  <div className="nax-overview-scroll" ref={overviewScrollRef} onWheel={onOverviewWheel}>
                    <div
                      className="nax-overview-points"
                    >
                      {overviewColumns.map((row, idx) => {
                        const m = parseMonthValue(row?.month);
                        const showYear = idx === 0 || m.format("MM") === "01";
                        return (
                        <button
                          key={`dot-${row.month}`}
                          type="button"
                          data-month={row?.month}
                          className={`nax-overview-dot ${getTimelineVisualClass(row)} ${row?.month === selectedMonth ? "selected" : ""} ${isCurrentMonth(row?.month) ? `is-current-month current-${getTimelineVisualClass(row)}` : ""}`}
                          onClick={() => handleMonthSelection(row?.month)}
                          aria-label={`Select ${moment(row?.month, "YYYY-MM").format("MMM YYYY")}`}
                          aria-current={row?.month === selectedMonth ? "true" : "false"}
                        >
                          {showYear ? <span className="nax-overview-point-year">{m.format("YYYY")}</span> : <span className="nax-overview-point-year placeholder"></span>}
                          <span className="nax-overview-point-date">
                            {m.format("MMM")}
                          </span>
                          <span className="nax-overview-point-node">
                            <i className={`${getTimelineDotIcon(row)} nax-overview-point-icon`}></i>
                          </span>
                          {row?.month === selectedMonth ? <span className="nax-overview-current-tag">Selected</span> : null}
                        </button>
                      );
                      })}
                    </div>
                  </div>
                  <div className="nax-overview-label">Non Adherent Months ({streak?.length || 0} MONTHS)</div>
                  <div className="nax-overview-years">
                    {overviewColumns.map((row, idx) => {
                      const month = moment(row?.month, "YYYY-MM").format("MM");
                      const year = moment(row?.month, "YYYY-MM").format("YYYY");
                      return <span key={`year-${row.month}`}>{idx === 0 || month === "01" ? year : ""}</span>;
                    })}
                  </div>
                  <div className="nax-overview-today">
                    <span>Today</span>
                    <i className="mdi mdi-circle"></i>
                  </div>
                </div>
              </div>
              <div className="nax-timeline-table-shell">
                {timelineColumns.length ? (
                  <>
                    <div className="nax-timeline-table-wrap" ref={timelineScrollRef}>
                      <table className="table nax-timeline-matrix">
                        <thead>
                          <tr>
                            <th>Components</th>
                            {timelineColumns.map((row) => (
                              <th
                                key={row.month}
                                data-month={row?.month}
                            className={`${row?.month === selectedMonth ? "selected-col-head" : ""} ${isCurrentMonth(row?.month) ? `current-col-head current-${getTimelineVisualClass(row)}` : ""}`}
                                onClick={() => handleMonthSelection(row?.month)}
                              >
                                {moment(row.month, "YYYY-MM").format("MMM YYYY")}
                                {row?.month === selectedMonth ? <span className="nax-current-tag">Selected</span> : null}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>Status</td>
                            {timelineColumns.map((row) => (
                              <td key={`s-${row.month}`} className={`${row?.month === selectedMonth ? "selected-col-cell" : ""} ${isCurrentMonth(row?.month) ? `current-col-cell current-${getTimelineVisualClass(row)}` : ""}`}>
                                <span className={`nax-status-pill ${getMonthStatusClass(row?.status)}`}>
                                  {isNonAdherentStatus(row?.status) ? "Non-Adherent" : "Adherent"}
                                </span>
                              </td>
                            ))}
                          </tr>
                          <tr>
                            <td>Technical ({technicalConfigCodes.join(", ") || "-"})</td>
                            {timelineColumns.map((row) => (
                              <td key={`t-${row.month}`} className={`${row?.month === selectedMonth ? "selected-col-cell" : ""} ${isCurrentMonth(row?.month) ? `current-col-cell current-${getTimelineVisualClass(row)}` : ""}`}>
                                {renderComponentCell(row, "technical", technicalConfigCodes)}
                              </td>
                            ))}
                          </tr>
                          <tr>
                            <td>Professional ({professionalConfigCodes.join(", ") || "-"})</td>
                            {timelineColumns.map((row) => (
                              <td key={`p-${row.month}`} className={`${row?.month === selectedMonth ? "selected-col-cell" : ""} ${isCurrentMonth(row?.month) ? `current-col-cell current-${getTimelineVisualClass(row)}` : ""}`}>
                                {renderComponentCell(row, "professional", professionalConfigCodes)}
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    {canScrollLeft ? (
                      <button
                        type="button"
                        className="nax-side-scroll-btn left"
                        aria-label="Scroll timeline left"
                        onClick={() => scrollTimeline(-1)}
                      >
                        <i className="mdi mdi-chevron-left"></i>
                      </button>
                    ) : null}
                    {canScrollRight ? (
                      <button
                        type="button"
                        className="nax-side-scroll-btn"
                        aria-label="Scroll timeline right"
                        onClick={() => scrollTimeline(1)}
                      >
                        <i className="mdi mdi-chevron-right"></i>
                      </button>
                    ) : null}
                  </>
                ) : (
                  <div className="mpr-empty-row">No monthly timeline data found for selected filter.</div>
                )}
              </div>
            </div>

            <div className="nax-bottom-grid">
              <div className="nax-card">
                <h6><i className="mdi mdi-fire"></i> Summary</h6>
                <div className="nax-mini-metrics">
                  {/* <div>
                    <span>Current Months</span>
                    <strong>{streak?.length || 0} Months</strong>
                    <small>
                      {streak?.start_month
                        ? `${moment(streak.start_month, "YYYY-MM").format("MMM YYYY")} - ${moment(streak.end_month, "YYYY-MM").format("MMM YYYY")}`
                        : "-"}
                    </small>
                  </div> */}
                  <div>
                    <span>Total Months</span>
                    <strong>{timelineColumns.length || 0} Months</strong>
                    <small>
                      {timelineColumns[0]?.month && timelineColumns[timelineColumns.length - 1]?.month
                        ? `${moment(timelineColumns[0].month, "YYYY-MM").format("MMM YYYY")} - ${moment(timelineColumns[timelineColumns.length - 1].month, "YYYY-MM").format("MMM YYYY")}`
                        : "-"}
                    </small>
                  </div>
                  <div>
                    <span>First Adherent Month</span>
                    <strong>
                      {allMonthsAdherent
                        ? "All months are adherent"
                        : lastAdherentRow?.month
                        ? moment(lastAdherentRow.month, "YYYY-MM").format("MMM YYYY")
                        : "-"}
                    </strong>
                    <small>
                      {allMonthsAdherent
                        ? "No non-adherent month found in timeline"
                        : lastAdherentRow
                        ? "Found in selected timeline"
                        : "No adherence in selected period"}
                    </small>
                  </div>
                </div>
              </div>

              <div className="nax-card">
                <h6><i className="mdi mdi-information-outline"></i> Legend</h6>
                <ul className="nax-list nax-legend-grid">
                  <li className="nax-legend-item">
                    <i className="mdi mdi-check-circle nax-legend-status-icon met"></i>
                    <span className="nax-legend-copy"><strong>Both Met (Adherent)</strong><small>Technical and Professional both met</small></span>
                  </li>
                  <li className="nax-legend-item">
                    <i className="mdi mdi-minus nax-legend-status-icon partial"></i>
                    <span className="nax-legend-copy"><strong>Partial</strong><small>Only one component met</small></span>
                  </li>
                  <li className="nax-legend-item">
                    <i className="mdi mdi-close-circle nax-legend-status-icon missing"></i>
                    <span className="nax-legend-copy"><strong>Missing Either</strong><small>Technical or Professional missing</small></span>
                  </li>
                  <li className="nax-legend-item">
                    <i className="mdi mdi-minus nax-legend-status-icon none"></i>
                    <span className="nax-legend-copy"><strong>No Activity</strong><small>No CPT activity for that month</small></span>
                  </li>
                  <li className="nax-legend-item">
                    <i className="mdi mdi-checkbox-blank nax-legend-status-icon streak"></i>
                    <span className="nax-legend-copy"><strong>In Current Month</strong><small>Highlights today’s month</small></span>
                  </li>
                </ul>
              </div>

              <div className="nax-card nax-howto-full">
                <h6><i className="mdi mdi-book-open-variant"></i> How to Use & Interpret This Report</h6>
                <div className="nax-howto-grid">
                  <div className="nax-howto-item">
                    <span className="nax-howto-label">1) Report Purpose</span>
                    <strong>Use this report to identify month-wise adherence and non-adherence based on both Technical and Professional CPT requirements.</strong>
                  </div>
                  <div className="nax-howto-item">
                    <span className="nax-howto-label">2) Filter Scope</span>
                    <strong>Top filters decide which patients and date range are loaded. Then click <em>View Report</em> in patient list to open timeline details for one patient.</strong>
                  </div>
                  <div className="nax-howto-item">
                    <span className="nax-howto-label">3) Technical CPT Logic</span>
                    <strong>{technicalConfigCodes.join(", ") || "-"}</strong>
                  </div>
                  <div className="nax-howto-item">
                    <span className="nax-howto-label">4) Professional CPT Logic</span>
                    <strong>{professionalConfigCodes.join(", ") || "-"}</strong>
                  </div>
                  <div className="nax-howto-item">
                    <span className="nax-howto-label">5) Month Status Calculation</span>
                    <strong>Adherent month = both components met. Non-Adherent month = either component missing. Partial = only one component met.</strong>
                  </div>
                  <div className="nax-howto-item">
                    <span className="nax-howto-label">6) Timeline Matrix Reading</span>
                    <strong>Each month column shows Technical and Professional CPT codes. Every code is shown with its own icon: check = met, close = not met.</strong>
                  </div>
                  <div className="nax-howto-item">
                    <span className="nax-howto-label">7) Legend & Current Month</span>
                    <strong>Legend states map to overview and timeline colors/icons: Both Met, Partial, Missing Either, No Activity, and In Current Month.</strong>
                  </div>
                  <div className="nax-howto-item">
                    <span className="nax-howto-label">8) Summary Cards</span>
                    <strong>Use Total Months and Last Adherent Month to quickly understand adherence trend and where follow-up is needed.</strong>
                  </div>
                  <div className="nax-howto-item">
                    <span className="nax-howto-label">9) Export Data</span>
                    <strong>Export CSV downloads month-level timeline rows with status and CPT code-level met/not-met details for current patient.</strong>
                  </div>
                  <div className="nax-howto-item">
                    <span className="nax-howto-label">10) Suggested Workflow</span>
                    <strong>Filter list → open patient timeline → review repeated missing months → verify code-level gaps → export and share action items.</strong>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default NonAdherentPatientTimelineReport;
